/**
 * Lead form (create + edit) — Phase 3B.
 *
 * Single-screen form for both /realtor/leads/new and
 * /realtor/leads/:id/edit. Required: name, phone, type. Everything
 * else is optional. Budget input mirrors the listings capture form's
 * rupee parsing (lakh / crore shorthand → paise on submit).
 *
 * Pipeline stage is intentionally NOT in this form — stage transitions
 * flow through the kanban menu (and hit the dedicated /stage endpoint
 * so stage_changed_at gets stamped).
 */

import { ArrowLeft, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { SearchableMultiSelect } from "@/features/realtor/components/searchable-multi-select";
import { useListingLinks } from "@/features/realtor/hooks/use-listing-links";
import {
	type CreateLeadInput,
	createLead,
	getMyLead,
	type Lead,
	type LeadTemperature,
	type LeadType,
	updateLead,
} from "@/features/realtor/services/leads-api.service";
import {
	type Listing,
	listMyListings,
} from "@/features/realtor/services/listings-api.service";
import {
	formatPaiseAsRupeeInput,
	parseRupeeInputToPaise,
} from "@/features/realtor/utils/listings-format";
import { StudioButton } from "@/features/studio/theme";

const TEMPERATURES: LeadTemperature[] = ["hot", "warm", "cold"];
const LEAD_TYPES: LeadType[] = ["buyer", "seller"];
const MAX_LINKED_LISTINGS = 10;

interface FormState {
	name: string;
	phone: string;
	email: string;
	type: LeadType;
	source: string;
	budgetMinInput: string;
	budgetMaxInput: string;
	temperature: LeadTemperature;
	notes: string;
	tagsInput: string;
	listingIds: string[];
}

const EMPTY_FORM: FormState = {
	name: "",
	phone: "",
	email: "",
	type: "buyer",
	source: "",
	budgetMinInput: "",
	budgetMaxInput: "",
	temperature: "warm",
	notes: "",
	tagsInput: "",
	listingIds: [],
};

export function LeadFormPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const isEdit = Boolean(id);
	const [isLoading, setIsLoading] = useState(isEdit);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [form, setForm] = useState<FormState>(EMPTY_FORM);
	const [errors, setErrors] = useState<
		Partial<Record<keyof FormState, string>>
	>({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isReconciling, setIsReconciling] = useState(false);
	const [listings, setListings] = useState<Listing[]>([]);
	// The linked-listing set as it was last persisted, used to diff on save
	// (the edit path reconciles link/unlink calls against this).
	const initialListingIdsRef = useRef<string[]>([]);
	// Flipped on unmount so a save that resolves after the user navigated
	// away can't force-navigate them back or setState on a dead component.
	const cancelledRef = useRef(false);
	const { reconcileLeadListings } = useListingLinks();

	useEffect(() => {
		cancelledRef.current = false;
		return () => {
			cancelledRef.current = true;
		};
	}, []);

	useEffect(() => {
		if (!isEdit || !id) return;
		let cancelled = false;
		(async () => {
			const result = await getMyLead(id);
			if (cancelled) return;
			if (!result.success || !result.data) {
				setLoadError(result.error ?? t("realtor.leads.loadFailedFallback"));
				setIsLoading(false);
				return;
			}
			const linkedIds = result.data.listings.map((l) => l.id);
			initialListingIdsRef.current = linkedIds;
			setForm(toFormState(result.data.lead, linkedIds));
			setIsLoading(false);
		})();
		return () => {
			cancelled = true;
		};
	}, [id, isEdit, t]);

	// The listings picker options — loaded for both create and edit. A
	// failure here just leaves the multi-select empty/disabled; it never
	// blocks the core lead form.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			const result = await listMyListings();
			if (cancelled) return;
			if (result.success && result.data) setListings(result.data);
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
		setErrors((prev) => {
			if (!prev[key]) return prev;
			const next = { ...prev };
			delete next[key];
			return next;
		});
	};

	const listingLabel = (listingId: string): string =>
		listings.find((l) => l.id === listingId)?.headline ?? listingId;

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const { errors: fieldErrors, payload } = validateForm(form, t);
		setErrors(fieldErrors);
		if (Object.keys(fieldErrors).length > 0) {
			toast.error(t("realtor.leads.form.invalidToast"));
			return;
		}
		setIsSubmitting(true);

		// CREATE — atomic: listingIds are linked in the same transaction as
		// the lead insert, so there's no partial-failure window here.
		if (!isEdit || !id) {
			const result = await createLead({
				...payload,
				listingIds: form.listingIds,
			});
			// If the user navigated away while the create was in flight, drop
			// everything: no toast, no navigate, no setState on a dead component.
			if (cancelledRef.current) return;
			setIsSubmitting(false);
			if (!result.success || !result.data) {
				toast.error(
					result.error ?? t("realtor.leads.form.submitErrorFallback"),
				);
				return;
			}
			toast.success(t("realtor.leads.form.createdToast"));
			navigate(`/realtor/leads/${result.data.id}`);
			return;
		}

		// EDIT — non-atomic: updateLead can't touch links, so the selected-
		// listings diff is reconciled via separate link/unlink calls AFTER
		// the lead update succeeds.
		const result = await updateLead(id, payload);
		if (cancelledRef.current) return;
		if (!result.success || !result.data) {
			setIsSubmitting(false);
			toast.error(result.error ?? t("realtor.leads.form.submitErrorFallback"));
			return;
		}

		setIsReconciling(true);
		const { ok, failures } = await reconcileLeadListings(
			id,
			initialListingIdsRef.current,
			form.listingIds,
		);
		setIsReconciling(false);
		if (cancelledRef.current) return;

		if (!ok) {
			// Silent-data-loss guard: some link/unlink calls failed. Do NOT
			// navigate away with the plain "updated" toast. Re-fetch the lead
			// to resync the form to what actually persisted, then surface a
			// distinct partial-failure toast that names the offending listings.
			const fresh = await getMyLead(id);
			if (cancelledRef.current) return;
			if (fresh.success && fresh.data) {
				const freshIds = fresh.data.listings.map((l) => l.id);
				initialListingIdsRef.current = freshIds;
				setForm(toFormState(fresh.data.lead, freshIds));
				const failedNames = failures
					.map((f) => listingLabel(f.listingId))
					.join(", ");
				toast.error(
					t("realtor.leads.form.linksPartialFailToast", {
						listings: failedNames,
					}),
				);
			} else {
				// The resync fetch ALSO failed — we can't confirm what actually
				// persisted, and the stale baseline in initialListingIdsRef would
				// cause a later Save to re-diff (re-issuing writes for changes that
				// already landed). Surface a distinct "reload before editing"
				// message instead of the normal partial-fail toast, and leave the
				// baseline untouched rather than trusting it. Also lock the form
				// behind the same load-error state the initial load uses, so the
				// user can't edit/Save again against the stale baseline until they
				// reload the page.
				const resyncMessage = t("realtor.leads.form.resyncFailedToast");
				toast.error(resyncMessage);
				setLoadError(resyncMessage);
			}
			setIsSubmitting(false);
			return;
		}

		initialListingIdsRef.current = [...form.listingIds];
		setIsSubmitting(false);
		toast.success(t("realtor.leads.form.updatedToast"));
		navigate(`/realtor/leads/${result.data.id}`);
	};

	if (isLoading) {
		return (
			<div
				className="bg-dashboard-surface min-h-screen p-6 space-y-3"
				data-testid="lead-form-loading"
			>
				<Skeleton className="h-12 w-full bg-dashboard-bg" />
				<Skeleton className="h-48 w-full bg-dashboard-bg" />
				<Skeleton className="h-48 w-full bg-dashboard-bg" />
			</div>
		);
	}

	if (loadError) {
		return (
			<div className="p-6 bg-dashboard-surface min-h-screen">
				<button
					type="button"
					onClick={() => navigate("/dashboard")}
					className="flex items-center gap-2 text-sm text-dashboard-muted hover:text-dashboard-text transition-colors mb-4"
				>
					<ArrowLeft className="h-4 w-4" />
					Back to dashboard
				</button>
				<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
					<CardContent className="p-6 text-center space-y-3">
						<p className="text-sm text-dashboard-muted">{loadError}</p>
						<StudioButton
							variant="secondary"
							onClick={() => navigate("/dashboard")}
						>
							{t("realtor.leads.backToList")}
						</StudioButton>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="space-y-6 p-6 bg-dashboard-surface min-h-screen"
			noValidate
		>
			{/* Back button */}
			<button
				type="button"
				onClick={() => navigate("/dashboard")}
				className="flex items-center gap-2 text-sm text-dashboard-muted hover:text-dashboard-text transition-colors"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to dashboard
			</button>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-6 space-y-1">
					<p
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 11,
							fontWeight: 600,
							letterSpacing: "0.18em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
						}}
					>
						{isEdit ? "EDIT LEAD" : "NEW LEAD"}
					</p>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontSize: 32,
							fontWeight: 500,
							fontStyle: "italic",
							color: "var(--color-dashboard-text)",
							marginTop: 8,
							lineHeight: 1.1,
						}}
					>
						{isEdit ? (
							<>Update lead details</>
						) : (
							<>
								Add a new{" "}
								<span
									style={{ color: "var(--color-dashboard-accent-display)" }}
								>
									lead
								</span>
							</>
						)}
					</h1>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 14,
							color: "var(--color-dashboard-muted)",
							marginTop: 8,
						}}
					>
						{t("realtor.leads.form.subtitle")}
					</p>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-5">
					<FormField
						id="name"
						label={t("realtor.leads.form.nameLabel")}
						error={errors.name}
					>
						<Input
							id="name"
							value={form.name}
							onChange={(e) => setField("name", e.target.value)}
							placeholder={t("realtor.leads.form.namePlaceholder")}
							maxLength={200}
							aria-invalid={errors.name ? true : undefined}
						/>
					</FormField>

					<div className="grid grid-cols-2 gap-3">
						<FormField
							id="phone"
							label={t("realtor.leads.form.phoneLabel")}
							error={errors.phone}
						>
							<Input
								id="phone"
								type="tel"
								inputMode="tel"
								value={form.phone}
								onChange={(e) => setField("phone", e.target.value)}
								placeholder="9876543210"
								aria-invalid={errors.phone ? true : undefined}
							/>
						</FormField>
						<FormField
							id="type"
							label={t("realtor.leads.form.typeLabel")}
							error={errors.type}
						>
							<Select
								value={form.type}
								onValueChange={(v) => setField("type", v as LeadType)}
							>
								<SelectTrigger
									id="type"
									style={{
										backgroundColor: "var(--color-dashboard-paper)",
										borderColor: "var(--color-dashboard-border)",
										color: "var(--color-dashboard-text)",
									}}
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent
									style={{
										backgroundColor: "var(--color-dashboard-bg)",
										borderColor: "var(--color-dashboard-border)",
									}}
								>
									{LEAD_TYPES.map((leadType) => (
										<SelectItem key={leadType} value={leadType}>
											{t(`realtor.leads.types.${leadType}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</FormField>
					</div>

					<FormField
						id="email"
						label={t("realtor.leads.form.emailLabel")}
						error={errors.email}
					>
						<Input
							id="email"
							type="email"
							inputMode="email"
							value={form.email}
							onChange={(e) => setField("email", e.target.value)}
							placeholder="email@example.com"
							aria-invalid={errors.email ? true : undefined}
						/>
					</FormField>

					<FormField
						id="temperature"
						label={t("realtor.leads.form.temperatureLabel")}
						error={errors.temperature}
					>
						<Select
							value={form.temperature}
							onValueChange={(v) =>
								setField("temperature", v as LeadTemperature)
							}
						>
							<SelectTrigger
								id="temperature"
								style={{
									backgroundColor: "var(--color-dashboard-paper)",
									borderColor: "var(--color-dashboard-border)",
									color: "var(--color-dashboard-text)",
								}}
							>
								<SelectValue />
							</SelectTrigger>
							<SelectContent
								style={{
									backgroundColor: "var(--color-dashboard-bg)",
									borderColor: "var(--color-dashboard-border)",
								}}
							>
								{TEMPERATURES.map((temp) => (
									<SelectItem key={temp} value={temp}>
										{t(`realtor.leads.temperatures.${temp}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</FormField>
				</CardContent>
			</Card>

			<Card className="bg-dashboard-bg border-dashboard-border rounded-xl shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="p-5 space-y-5">
					<div className="grid grid-cols-2 gap-3">
						<FormField
							id="budgetMinInput"
							label={t("realtor.leads.form.budgetMinLabel")}
							error={errors.budgetMinInput}
						>
							<Input
								id="budgetMinInput"
								value={form.budgetMinInput}
								onChange={(e) => setField("budgetMinInput", e.target.value)}
								placeholder="1.5 Cr"
								aria-invalid={errors.budgetMinInput ? true : undefined}
							/>
						</FormField>
						<FormField
							id="budgetMaxInput"
							label={t("realtor.leads.form.budgetMaxLabel")}
							error={errors.budgetMaxInput}
						>
							<Input
								id="budgetMaxInput"
								value={form.budgetMaxInput}
								onChange={(e) => setField("budgetMaxInput", e.target.value)}
								placeholder="2 Cr"
								aria-invalid={errors.budgetMaxInput ? true : undefined}
							/>
						</FormField>
					</div>
					<FormField
						id="source"
						label={t("realtor.leads.form.sourceLabel")}
						error={errors.source}
						hint={t("realtor.leads.form.sourceHint")}
					>
						<Input
							id="source"
							value={form.source}
							onChange={(e) => setField("source", e.target.value)}
							placeholder="WhatsApp, Walk-in, Referral…"
						/>
					</FormField>
					<FormField
						id="listingIds"
						label={t("realtor.leads.form.listingsLabel")}
						hint={t("realtor.leads.form.listingsHint")}
					>
						<SearchableMultiSelect
							id="listingIds"
							aria-label={t("realtor.leads.form.listingsLabel")}
							options={listings.map((l) => ({
								value: l.id,
								label: l.headline,
								sublabel: `${l.locality} · ${l.city}`,
							}))}
							selected={form.listingIds}
							onChange={(next) => setField("listingIds", next)}
							max={MAX_LINKED_LISTINGS}
							triggerPlaceholder={t(
								"realtor.leads.form.listingsTriggerPlaceholder",
							)}
							triggerLabel={(count) =>
								t("realtor.leads.form.listingsSelectedCount", { count })
							}
							searchPlaceholder={t(
								"realtor.leads.form.listingsSearchPlaceholder",
							)}
							noResultsText={t("realtor.leads.form.listingsNoResults")}
							noOptionsText={t("realtor.leads.form.listingsNoneAvailable")}
							capReachedText={t("realtor.leads.form.listingsCapReached", {
								max: MAX_LINKED_LISTINGS,
							})}
						/>
					</FormField>

					<FormField
						id="tagsInput"
						label={t("realtor.leads.form.tagsLabel")}
						error={errors.tagsInput}
						hint={t("realtor.leads.form.tagsHint")}
					>
						<Input
							id="tagsInput"
							value={form.tagsInput}
							onChange={(e) => setField("tagsInput", e.target.value)}
							placeholder="vip, urgent"
						/>
					</FormField>
					<FormField
						id="notes"
						label={t("realtor.leads.form.notesLabel")}
						error={errors.notes}
					>
						<Textarea
							id="notes"
							value={form.notes}
							onChange={(e) => setField("notes", e.target.value)}
							rows={3}
							maxLength={5000}
						/>
					</FormField>
				</CardContent>
			</Card>

			<div className="flex justify-end gap-2">
				<StudioButton
					variant="secondary"
					onClick={() => navigate("/dashboard")}
				>
					{t("realtor.leads.form.cancel")}
				</StudioButton>
				<StudioButton type="submit" disabled={isSubmitting || isReconciling}>
					<Save className="h-4 w-4" />
					{isSubmitting || isReconciling
						? t("realtor.leads.form.submitting")
						: isEdit
							? t("realtor.leads.form.saveChanges")
							: t("realtor.leads.form.createLead")}
				</StudioButton>
			</div>
		</form>
	);
}

interface FormFieldProps {
	id: string;
	label: string;
	error?: string;
	hint?: string;
	children: React.ReactNode;
}

function FormField({ id, label, error, hint, children }: FormFieldProps) {
	const errorId = error ? `${id}-error` : undefined;
	return (
		<div className="space-y-1.5">
			<Label htmlFor={id} className="text-dashboard-text">
				{label}
			</Label>
			{children}
			{error && (
				<p
					id={errorId}
					className="text-xs text-dashboard-pill-risk"
					role="alert"
				>
					{error}
				</p>
			)}
			{!error && hint && <p className="text-xs text-dashboard-muted">{hint}</p>}
		</div>
	);
}

function toFormState(lead: Lead, listingIds: string[]): FormState {
	return {
		name: lead.name,
		phone: lead.phone,
		email: lead.email ?? "",
		type: lead.type,
		source: lead.source ?? "",
		budgetMinInput:
			lead.budgetMin != null ? formatPaiseAsRupeeInput(lead.budgetMin) : "",
		budgetMaxInput:
			lead.budgetMax != null ? formatPaiseAsRupeeInput(lead.budgetMax) : "",
		temperature: lead.temperature,
		notes: lead.notes ?? "",
		tagsInput: lead.tags.join(", "),
		listingIds,
	};
}

interface ValidatedForm {
	errors: Partial<Record<keyof FormState, string>>;
	payload: CreateLeadInput;
}

function validateForm(
	form: FormState,
	t: (k: string, opts?: Record<string, unknown>) => string,
): ValidatedForm {
	const errors: Partial<Record<keyof FormState, string>> = {};

	const name = form.name.trim();
	if (!name) errors.name = t("realtor.leads.form.errors.name");

	const phone = form.phone.trim();
	if (!phone) {
		errors.phone = t("realtor.leads.form.errors.phone");
	} else if (!/^\+?\d[\d\s-]{6,20}$/.test(phone)) {
		errors.phone = t("realtor.leads.form.errors.phoneInvalid");
	}

	const email = form.email.trim();
	if (email) {
		// Lightweight email check; backend Zod also validates.
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			errors.email = t("realtor.leads.form.errors.email");
		}
	}

	let budgetMin: number | null = null;
	let budgetMax: number | null = null;
	if (form.budgetMinInput.trim()) {
		const parsed = parseRupeeInputToPaise(form.budgetMinInput);
		if (parsed == null) {
			errors.budgetMinInput = t("realtor.leads.form.errors.budget");
		} else {
			budgetMin = parsed;
		}
	}
	if (form.budgetMaxInput.trim()) {
		const parsed = parseRupeeInputToPaise(form.budgetMaxInput);
		if (parsed == null) {
			errors.budgetMaxInput = t("realtor.leads.form.errors.budget");
		} else {
			budgetMax = parsed;
		}
	}
	if (budgetMin != null && budgetMax != null && budgetMax < budgetMin) {
		errors.budgetMaxInput = t("realtor.leads.form.errors.budgetRange");
	}

	const tags = form.tagsInput
		.split(",")
		.map((s) => s.trim())
		.filter(Boolean);

	const payload: CreateLeadInput = {
		name,
		phone,
		email: email || null,
		type: form.type,
		source: form.source.trim() || null,
		budgetMin,
		budgetMax,
		temperature: form.temperature,
		notes: form.notes.trim() || null,
		tags,
	};

	return { errors, payload };
}
